import { useEffect } from "react";
import { Link } from "react-router-dom";

// Terms and Conditions — public marketing page paired with /privacy.
// Owner ask 2026-08-28: "Need Terms and Conditions". Written to match
// the SaaS + real-estate acquisitions context of FlipPilot (softphone
// dialer, CRM, offer PDFs, SignalWire A2P messaging). Reads in plain
// English so sellers who land here from a text-message opt-in can
// understand what they agreed to.
export default function TermsAndConditions() {
  useEffect(() => {
    document.title = "Terms and Conditions · FlipPilot";
  }, []);

  const updated = "August 28, 2026";

  return (
    <div className="mkt-container py-16 md:py-24" data-testid="terms-page">
      <div className="max-w-3xl mx-auto">
        <span
          className="inline-block text-xs uppercase tracking-widest px-3 py-1 rounded-full"
          style={{ background: "var(--mkt-surface-2)", color: "var(--mkt-accent-dark)" }}
        >
          Legal
        </span>
        <h1 className="mkt-hero-title mt-6 leading-tight">Terms and Conditions</h1>
        <p className="mt-4 text-sm text-[var(--mkt-text-muted)]">
          Last updated: {updated}
        </p>

        <section className="mt-10 space-y-6 text-[15px] leading-relaxed text-[var(--mkt-text)]">
          <p>
            These Terms and Conditions (&ldquo;<b>Terms</b>&rdquo;) govern your
            access to and use of the FlipPilot software-as-a-service platform,
            the website at{" "}
            <a href="https://flippilot.us" className="underline">flippilot.us</a>{" "}
            (the &ldquo;<b>Site</b>&rdquo;), and any related applications,
            APIs, dashboards, or telephony services (collectively, the
            &ldquo;<b>Service</b>&rdquo;) offered by Flip Pilot LLC
            (&ldquo;<b>FlipPilot</b>&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;,
            or &ldquo;us&rdquo;). By creating an account, clicking &ldquo;I
            agree&rdquo;, or otherwise using the Service, you agree to these
            Terms. If you are entering into these Terms on behalf of a
            company, you represent that you have authority to bind that
            company.
          </p>

          <h2 className="text-xl font-semibold mt-8">1. Eligibility & Account</h2>
          <p>
            You must be at least 18 years old and legally able to enter a
            binding contract. You agree to (a) provide accurate information
            when creating your account, (b) keep your login credentials
            secure, and (c) be responsible for any activity on your
            account. Sharing an account across multiple users is not
            permitted &mdash; each seat is per-user.
          </p>

          <h2 className="text-xl font-semibold mt-8">2. Subscription, Billing & ECU Usage</h2>
          <p>
            The Service is billed monthly or annually as described in your
            order form. Telephony minutes, SMS segments, and AI-inference
            usage are billed at the metered rates published on the Site or
            in your admin dashboard. You are responsible for all charges
            incurred under your account, including usage by your team
            members. Fees are non-refundable except where required by
            applicable law.
          </p>

          <h2 className="text-xl font-semibold mt-8">3. Acceptable Use</h2>
          <p>You agree <b>not</b> to use the Service to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Send unsolicited calls or texts in violation of the TCPA,
                CAN-SPAM, or any A2P/10DLC rules of the receiving carrier.</li>
            <li>Contact anyone on the National Do Not Call registry, your
                internal DNC list, or anyone who has revoked consent.</li>
            <li>Misrepresent your identity or the property you are
                inquiring about.</li>
            <li>Scrape, resell, or reverse-engineer FlipPilot data or the
                Service itself.</li>
            <li>Upload lead lists you do not have the legal right to call
                or text under applicable state and federal law.</li>
          </ul>
          <p>
            You are solely responsible for the content of every call, text,
            email, and offer document you send through the Service. We
            reserve the right to suspend or terminate accounts that abuse
            the platform or generate carrier complaints.
          </p>

          <h2 className="text-xl font-semibold mt-8">4. SMS / Voice Consent</h2>
          <p>
            When you use FlipPilot to send texts or place calls, you
            represent that you have valid consent from each recipient under
            the TCPA and applicable state law. Message and data rates may
            apply to your recipients. Recipients can reply <b>STOP</b> to
            opt out at any time and <b>HELP</b> for support. Our A2P/10DLC
            campaign details are published on our{" "}
            <Link to="/privacy" className="underline">Privacy Policy</Link> page.
          </p>

          <h2 className="text-xl font-semibold mt-8">5. Third-Party Services</h2>
          <p>
            The Service integrates with third-party providers (including
            SignalWire, Twilio, Retell AI, SignWell, Stripe, Google, and
            others). Your use of those integrations is subject to each
            provider&rsquo;s own terms. We are not responsible for outages,
            billing, or policy changes on third-party platforms.
          </p>

          <h2 className="text-xl font-semibold mt-8">6. Your Data</h2>
          <p>
            You retain all rights to the leads, recordings, contracts,
            offers, and other content you upload or generate through the
            Service (&ldquo;<b>Customer Data</b>&rdquo;). You grant us a
            worldwide, non-exclusive license to host, transmit, and process
            Customer Data solely to provide the Service. On termination we
            will make Customer Data available for export for 30 days, after
            which we may delete it. See our{" "}
            <Link to="/privacy" className="underline">Privacy Policy</Link> for
            how we handle personal data.
          </p>

          <h2 className="text-xl font-semibold mt-8">7. Cash Offers & Contracts</h2>
          <p>
            FlipPilot generates cash-offer PDFs, purchase agreements, and
            related documents based on inputs you provide. These documents
            are templates &mdash; you are responsible for reviewing them for
            legal accuracy before sending to a seller and for complying
            with real-estate licensing rules in your jurisdiction.
            FlipPilot is not a law firm, brokerage, or licensed real-estate
            agent, and nothing in the Service constitutes legal advice.
          </p>

          <h2 className="text-xl font-semibold mt-8">8. Intellectual Property</h2>
          <p>
            The Service, including all software, designs, workflows,
            trademarks, and content we create, is owned by FlipPilot and
            protected by U.S. and international IP law. We grant you a
            limited, non-exclusive, non-transferable license to use the
            Service for your internal business purposes during your
            subscription. All rights not expressly granted are reserved.
          </p>

          <h2 className="text-xl font-semibold mt-8">9. Disclaimers</h2>
          <p>
            The Service is provided on an <b>&ldquo;AS IS&rdquo;</b> and{" "}
            <b>&ldquo;AS AVAILABLE&rdquo;</b> basis. To the maximum extent
            permitted by law, we disclaim all warranties, express or
            implied, including merchantability, fitness for a particular
            purpose, and non-infringement. We do not guarantee that the
            Service will be uninterrupted, error-free, or that any specific
            outcome (deals closed, leads converted, etc.) will result from
            using it.
          </p>

          <h2 className="text-xl font-semibold mt-8">10. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, FlipPilot&rsquo;s
            aggregate liability arising out of or relating to these Terms
            or the Service shall not exceed the amount you paid to us in
            the twelve months preceding the event giving rise to the
            claim. In no event shall we be liable for indirect,
            incidental, consequential, special, or punitive damages,
            including lost profits, lost data, or business interruption,
            even if advised of the possibility of such damages.
          </p>

          <h2 className="text-xl font-semibold mt-8">11. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold FlipPilot harmless
            from any claims, damages, or expenses (including reasonable
            attorney fees) arising from (a) your use of the Service, (b)
            your Customer Data, (c) your violation of these Terms, or (d)
            your violation of any applicable law or the rights of a third
            party (including TCPA / DNC violations).
          </p>

          <h2 className="text-xl font-semibold mt-8">12. Termination</h2>
          <p>
            You may cancel your subscription at any time from your account
            dashboard. We may suspend or terminate your account immediately
            if you materially breach these Terms, fail to pay fees, or use
            the Service in a way that risks harm to FlipPilot or third
            parties. Sections 3, 6-11, and 13-15 survive termination.
          </p>

          <h2 className="text-xl font-semibold mt-8">13. Changes to the Terms</h2>
          <p>
            We may update these Terms from time to time. Material changes
            will be posted here with an updated &ldquo;Last updated&rdquo;
            date and, where practical, emailed to your account admin.
            Continued use of the Service after the effective date
            constitutes acceptance of the new Terms.
          </p>

          <h2 className="text-xl font-semibold mt-8">14. Governing Law & Disputes</h2>
          <p>
            These Terms are governed by the laws of the State of Florida,
            without regard to conflict-of-laws rules. Any dispute not
            resolved informally within 30 days shall be submitted to
            binding arbitration in Lee County, Florida, under the
            commercial rules of the American Arbitration Association. You
            waive any right to participate in a class action.
          </p>

          <h2 className="text-xl font-semibold mt-8">15. Contact</h2>
          <p>
            Questions about these Terms? Reach us at:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Email:{" "}
              <a href="mailto:legal@flippilot.us" className="underline"
                 data-testid="terms-contact-email">legal@flippilot.us</a>
            </li>
            <li>Phone:{" "}
              <a href="tel:+12394808400" className="underline"
                 data-testid="terms-contact-phone">+1 (239) 480-8400</a>
            </li>
            <li>Mail: Flip Pilot LLC, Cape Coral, FL</li>
          </ul>

          <div className="mt-10 pt-6 border-t border-[var(--mkt-line)] flex flex-wrap gap-4 text-sm">
            <Link to="/privacy" className="underline" data-testid="terms-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/contact" className="underline" data-testid="terms-contact-link">
              Contact us
            </Link>
            <Link to="/" className="underline" data-testid="terms-home-link">
              Back home
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
